import React from 'react';
import {
  LayoutDashboard,
  Workflow,
  FileText,
  Braces,
  Database,
  Book,
  FlaskConical,
  Zap,
  CloudUpload,
  Terminal,
  ShieldCheck,
  GitBranch,
  Settings,
  Globe,
  Share2,
  ArrowLeft,
  BrainCircuit,
  UserCog,
  Bot,
  PanelLeftOpen,
  PanelRightOpen,
} from 'lucide-react';

type SidebarItem = {
  label: string;
  icon: React.ElementType;
  isSecondary?: boolean;
};

type Props = {
  selected: {
    name: string;
    section: string;
    key?: string;
  };
  onSelect: (section: string) => void;
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  disabled: boolean;
};

const sidebarItems: SidebarItem[] = [
  { label: 'Overview', icon: LayoutDashboard },
  { label: 'Resources', icon: Book },
  { label: 'Builder', icon: Workflow },
  { label: 'Test Agent', icon: FlaskConical },
  { label: 'Traces & Logs', icon: Terminal },
  { label: 'Metrics', icon: Zap },
  { label: 'Deployments', icon: CloudUpload },
];

const AgentStudioSidebar: React.FC<Props> = ({ selected, onSelect, collapsed, setCollapsed,disabled }) => {
  const toggleSidebar = () => setCollapsed((prev) => !prev);

  return (
    <aside
      className={`${collapsed ? 'w-20' : 'w-64'
        } bg-neutral-950 border-r border-neutral-800 h-full text-sm text-gray-300 flex flex-col transition-all duration-200`}
    >
      {/* Header */}
      <div className={`px-4 py-4 border-b border-neutral-800 ${collapsed ? 'items-center' : ''}`}>
        {/* First line */}
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-2'} mb-1`}>
          <BrainCircuit className="w-6 h-6 text-indigo-500" />
          {!collapsed && (
            <div className="leading-tight">
              <p className="text-sm font-semibold text-white">Agent Studio</p>
            </div>
          )}
        </div>

        {/* Second line */}
        {!collapsed && (
          <div className="flex items-center space-x-2 mt-1">
            <Bot className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-300 font-normal">Compliance Agent -Edit Mode</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {sidebarItems.map(({ label, icon: Icon, isSecondary }) => (
          <div key={label}>
            {isSecondary && !collapsed && (
              <div className="border-t border-neutral-800 my-3 pt-3 text-[11px] text-gray-500 uppercase tracking-wide">
                Advanced
              </div>
            )}
            <button
              onClick={() => !disabled && onSelect(label)} // ✅ Disable clicks
              className={`w-full flex items-center ${collapsed ? 'justify-center' : ''
                } px-3 py-2 rounded-md text-left transition-all ${selected?.section === label && !disabled
                  ? 'bg-neutral-800 text-white'
                  : 'hover:bg-neutral-900'
                } ${isSecondary ? 'text-red-400' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              title={collapsed ? label : undefined}
              disabled={disabled} // ✅ HTML native disable
            >
              <Icon className={`w-5 h-5 ${collapsed ? '' : 'mr-3'} shrink-0`} />
              {!collapsed && <span className="truncate">{label}</span>}
            </button>
          </div>
        ))}
      </nav>

      {/* Collapse Toggle */}
      <div className="border-t border-neutral-800 p-2 flex justify-center">
        <button
          onClick={toggleSidebar}
          className="text-gray-400 hover:text-white transition"
          title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
        >
          {collapsed ? <PanelLeftOpen size={20} /> : <PanelRightOpen size={20} />}
        </button>
      </div>
    </aside>
  );
};

export default AgentStudioSidebar;
